// The desk flows' header affordance — shippra IssueBook/ReturnBook lines
// 203-217: a "Scan with phone" button that opens the pairing dialog, swapping
// to a green "Phone linked" pill with a pulsing dot once a device pairs (still
// opens the dialog, for the feed / disconnect). One component so Issue, Return
// and Add Book render the identical control.
import { Show, createSignal } from "solid-js";
import { Smartphone } from "lucide-solid";
import { Button } from "@erp/ui/button";
import { IconButton } from "@erp/ui/icon-button";
import { PhoneScanModal } from "./PhoneScanModal.jsx";
import { scanHub } from "./hub.js";
import { canFieldScan } from "./FieldScanButton.jsx";

export function ScanWithPhoneButton(props: {
  /** One line in the dialog saying what scans do on THIS screen. */
  contextHint?: string;
  id?: string;
  /**
   * ICON ONLY, for the app header (2026-10-02, the owner: pairing left the
   * sidebar and became a header control). Same component on purpose — this
   * file's whole reason for existing is that Issue, Return and the console
   * render the identical control, and a second one would drift. The linked
   * state still shows: the glyph takes the success ink and keeps the pulse.
   */
  compact?: boolean;
}) {
  // On a touch device the camera IS the scanner (FieldScanButton) — pairing a
  // phone to a phone is nonsense, so the CTA disappears wherever it is used.
  // ONE gate in ONE place: the same canFieldScan() that offers the field
  // button withdraws this one; the two affordances are exact complements.
  if (canFieldScan()) return null;

  const hub = scanHub();
  const [open, setOpen] = createSignal(false);
  const linked = (): boolean => hub.devices().length > 0;

  if (props.compact) {
    return (
      <>
        <IconButton
          id={props.id ?? "btn-scan-with-phone"}
          icon={(p) => (
            <span class="relative flex">
              <Smartphone {...p} />
              <Show when={linked()}>
                {/* The pulse rides the glyph rather than a label — the header
                    has no room for "Phone linked", and the dot is what carried
                    that state on the desk buttons anyway. */}
                <span class="absolute -right-0.5 -top-0.5 flex size-1.5" aria-hidden="true">
                  <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-success" />
                  <span class="relative inline-flex size-1.5 rounded-full bg-success" />
                </span>
              </Show>
            </span>
          )}
          label={linked() ? "Phone linked — open pairing" : "Scan with phone"}
          size="m"
          onClick={() => setOpen(true)}
        />
        <PhoneScanModal open={open()} onClose={() => setOpen(false)} contextHint={props.contextHint} />
      </>
    );
  }

  return (
    <>
      {/* Linked state keeps the btn's structural border, opaque success; the ground is the opaque success tint
          (the two-tone pair, DEV-GUIDE-002 §12.4) painted over the secondary's grey. The LABEL stays the
          secondary's body ink — green text is D4's breach; the pulsing dot beside it carries the hue. */}
      <Button
        id={props.id ?? "btn-scan-with-phone"}
        variant={linked() ? "secondary" : "primary"}
        class={`gap-2 ${linked() ? "border-success bg-success-tint" : ""}`}
        onClick={() => setOpen(true)}
      >
        <Show
          when={linked()}
          fallback={
            <>
              <Smartphone size={14} aria-hidden="true" />
              Scan with phone
            </>
          }
        >
          <span class="relative flex h-2 w-2" aria-hidden="true">
            {/* animate-ping's own keyframe fades the ring out from the opaque stop — no
                static opacity utility on top (DEV-GUIDE-002 §12.6). */}
            <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-success" />
            <span class="relative inline-flex h-2 w-2 rounded-full bg-success" />
          </span>
          Phone linked
        </Show>
      </Button>
      <PhoneScanModal open={open()} onClose={() => setOpen(false)} contextHint={props.contextHint} />
    </>
  );
}
